/* eslint-disable no-unused-expressions */

import every from 'lodash/every';
import isArray from 'lodash/isArray';
import some from 'lodash/some';

import {
  selectSingle,
  unselectSingle,
  setParent,
  collapseSingle,
  collapseSingleDescendant,
  uncollapseSingle,
  uncollapseSingleDescendant,
  removeSearch,
} from './modifyOption';

// Used on options, who have a `value` Array of Path objects: `{ value: 'HELLO WORLD' }`:
export const getOptionText = option => {
  const { value, label } = option.value[option.value.length - 1];
  return label || value;
};

export const getPathsTree = paths => {
  // First generate Maps so that we can ensure ordering is correct (e.g. if a path is defined much later that shares a root with a much earlier item)
  const root = {
    children: new Map(),
    depth: 0,
  };

  const getItemMap = (valueStr, value, map, isMaxDepth) => {
    if (map.has(valueStr)) {
      return map.get(valueStr);
    }
    const response = {
      children: isMaxDepth ? null : new Map(),
      value,
    };
    map.set(valueStr, response);
    return response;
  };

  (paths || []).forEach(path => {
    const maxDepth = path.length - 1;
    let currentMap = root;
    let valueArr = [];

    path.forEach((item, i) => {
      valueArr = valueArr.concat(item);
      currentMap = getItemMap(
        item.value,
        valueArr,
        currentMap.children,
        maxDepth === i,
      );
    });
  });

  return root;
};

/*
 * buildOptionsFromPaths is a utility method for converting "paths" into "Option" objects used in HierarchyMultiSelect
 *
 * The most common use-case for the Hierarchy multiselect is for our Property domain objects typed: "Hierarchy"
 * These come with a `paths` Array of Arrays, which looks like:
 *   `paths: [
 *      [{ value: 'a' }, { value: '1' }],
 *      [{ value: 'a' }, { value: '2' }],
 *      [{ value: 'b' }, { value: '3' }],
 *    ]`
 *
 * This helper attempts to (reasonably efficiently) map those into:
 *   `options: [
 *      { // $OPTION_1
 *        value: [{ value: 'a' }], // these Objects are references to the original paths to save memory, so is actually just array of int references
 *        parent: null, // References parent (if not a root)
 *        children: [$OPTION_2, $OPTION_3], // References to next items
 *        // ... more props and flags here like selected/partialSelected/collapsed/highlightStart/highlightEnd/etc.
 *      },
 *      { // $OPTION_2
 *        value: [{ value: 'a' }, { value: '1' }], // these Objects are references to the original paths to save memory
 *        parent: $OPTION_1, // References parent (if not a root)
 *        children: null,
 *        // ... more props and flags here like selected/partialSelected/collapsed/highlightStart/highlightEnd/etc.
 *      },
 *      { // $OPTION_3
 *        value: [{ value: 'a' }, { value: '2' }], // these Objects are references to the original paths to save memory
 *        parent: $OPTION_1, // References parent (if not a root)
 *        children: null,
 *        // ... more props and flags here like selected/partialSelected/collapsed/highlightStart/highlightEnd/etc.
 *      },
 *    ]`
 *
 * The philosophy here is to only use _references_ when at all possible because they are cheap, as well as booleans and ints for everything else.
 */
export const buildOptionsFromPaths = paths => {
  // Maps generated, convert to options (including parent nodes):
  //   TODO: sort? Currently at least Chrome already keeps them sorted, but need
  //   to `reverse()` for push/pop, since pop takes the last item
  const getChildrenArr = children => Array.from(children.values()).reverse();
  const stack = getChildrenArr(getPathsTree(paths).children);
  const options = [];

  while (stack.length > 0) {
    const { children, value, parent } = stack.pop();
    const option = {
      value,
      // `parent` allows handling nested "selected/partial/unselected" state much more quickly using very little memory.
      // Just don't try to stringify or recursively parse this Object...
      parent,
      selected: false,
      partialSelected: false,
    };

    if (children) {
      // Children simplifies selecting subsets
      option.children = [];
      getChildrenArr(children).forEach(child => {
        setParent(child, option);
        stack.push(child);
      });
    }

    options.push(option);
    if (parent) {
      parent.children.push(option);
    }
  }

  return options;
};

export const isLeaf = option => !option.children;
export const getValue = options => options.filter(o => isLeaf(o) && o.selected);

export const isFullSelected = option =>
  option.selected && !option.partialSelected;
export const isPartial = option => option.partialSelected;
export const isSelected = option => option.selected; // true for partial _or_ full

// Handles the selected/partial/unselected status on the wrapper (ancestor) nodes so that they all reflect the value leaves.
// This needs to happen as a scan / util function since searching (and unsearching) can affect the results. e.g. Maybe all
// searched descendants of a node are selected, but when removing the search query a new descendant appears who is not selected.
//
// When not searching, or when the value has already been determined, skip filtering down the leafs.
export const setSelectedStatusForAncestors = (
  searchedOptions,
  value = searchedOptions.filter(isLeaf),
) => {
  const stack = [];
  const parentsSeen = new Set();
  const addOptionParentToStackIfExists = ({ parent }) => {
    if (parent && !parentsSeen.has(parent)) {
      // unshift because a parent relies on all of its children being complete.
      // Basically we start with all leaves complete. This is the base case. So we
      // iterate those leaves and add their parents. As we progress, we unshift the parent's parent
      // to the end, so any sibling parents are completed first (working from the deepest to shallowest items)
      stack.unshift(parent);
      parentsSeen.add(parent);
    }
  };

  value.forEach(addOptionParentToStackIfExists);

  while (stack.length > 0) {
    const option = stack.pop();
    const { children, searchVisible } = option;

    if (children) {
      const relevantChildren = searchVisible
        ? children.filter(o => o.searchVisible)
        : children;

      if (every(relevantChildren, isFullSelected)) {
        option.selected = true;
        option.partialSelected = false;
      } else if (some(relevantChildren, isSelected)) {
        option.selected = true;
        option.partialSelected = true;
      } else {
        option.selected = false;
        option.partialSelected = false;
      }
    }
    addOptionParentToStackIfExists(option); // Will not add if already at top level
  }
};

function applyToDescendants(option, methodToApply, searchedDescendantsOnly) {
  if (option.children) {
    const shouldApply = o => o.searchVisible || !searchedDescendantsOnly;

    const stack = option.children.filter(shouldApply);
    while (stack.length) {
      const child = stack.pop();
      methodToApply(child);
      if (child.children) {
        child.children.forEach(grandchild => {
          if (shouldApply(grandchild)) {
            stack.push(grandchild);
          }
        });
      }
    }
  }
}

function applyToOptionAndDescendants(
  option,
  methodToApply,
  searchedDescendantsOnly,
) {
  methodToApply(option);
  applyToDescendants(option, methodToApply, searchedDescendantsOnly);
}

function updateAncestorsSelectionState(option) {
  // Slight hack, but `setSelectedStatusForAncestors` basically bubbles up to all ancestors of the given values.
  // So sending it just the _current_ option reuses that code:
  setSelectedStatusForAncestors([option], [option]);
}

// Used when clicking a `checked` (full, not partial) checkbox on Options, or when clicking the X on a value:
export const unselect = option => {
  /*
   * Use `searchVisible` as a heuristic to tell if we're searching.
   * This is because when search is removed, that flag is nulled out.
   * It cannot be false/null for an item clicked, because those items are invisible
   * (Except for items in the Values panel which are always visible, but those are leaf nodes anyway)
   */
  const searchedDescendantsOnly = !!option.searchVisible;
  applyToOptionAndDescendants(option, unselectSingle, searchedDescendantsOnly);
  updateAncestorsSelectionState(option);
};

// Used when clicking an unchecked checkbox on Options panel:
export const select = option => {
  // See `unselect` for details about "searchedDescendantsOnly"
  const searchedDescendantsOnly = !!option.searchVisible;
  applyToOptionAndDescendants(option, selectSingle, searchedDescendantsOnly);
  updateAncestorsSelectionState(option);
};

// Used when clicking a collapse toggle on a non-collapsed option:
export const collapse = option => {
  collapseSingle(option);
  applyToDescendants(option, collapseSingleDescendant);
};

// Used when clicking a collapse toggle on a collapsed option (e.g. to expand):
export const uncollapse = option => {
  uncollapseSingle(option);
  applyToDescendants(option, uncollapseSingleDescendant);
};

const uncollapseReset = option => {
  uncollapse(option);
  removeSearch(option);
};

const collapseReset = option => {
  collapse(option);
  removeSearch(option);
};

const isRoot = option => option.value.length === 1;
// Used when clicking the top-level collapse if not everything is already collapsed:
export const collapseAllRoots = options => {
  options.forEach(option => {
    if (isRoot(option)) {
      collapseSingle(option);
    } else {
      collapseSingleDescendant(option);
    }
  });
};

// Used when clicking the top-level collapse if everything is already collapsed:
export const uncollapseAllRoots = options => {
  options.forEach(uncollapseSingleDescendant);
};

// collapse all options that are not selected and remove search props
export const uncollapseAllSelectedAndRemoveSearch = options => {
  options.forEach(option =>
    option.selected ? uncollapseReset(option) : collapseReset(option),
  );
};

export const allRootsAreCollapsed = options =>
  every(options, option => option.collapsed || !isRoot(option));

export const CHECKBOX_STATE = {
  CHECKED: 'checked',
  PARTIAL: 'partial',
  UNCHECKED: 'unchecked',
};

export function setOptionsSelectedToValue(options, value) {
  // Sync to value. Originally used a `new Set(value)` and used that to decide whether to select/unselect an option...
  // Two passes (disable everything then enable just the values) is much faster.
  options.forEach(unselectSingle);
  value.forEach(selectSingle);
  setSelectedStatusForAncestors(value, value);
}

// SLOW avoid when possible. Preferrably only do this if an actual external thing changes the value for real, or on init:
export function syncToExternalValue(options, value, onlyLeaves = true) {
  options.forEach(unselectSingle);
  if (!isArray(value) || value.length === 0) {
    return [];
  }

  const valuePaths = getPathsTree(value);
  const leaves = options.filter(isLeaf);
  const optionsToCheck = options;

  // TODO: can potentially save computations when there are few values by iterating Root elements instead and shorting out early:
  optionsToCheck.forEach(option => {
    let node = valuePaths;
    const { value: optionVal } = option;

    for (let i = 0; i < optionVal.length; i += 1) {
      const levelVal = optionVal[i] && optionVal[i].value;
      if (node.children && node.children.has(levelVal)) {
        node = node.children.get(levelVal);
      } else {
        return; // exit, this one is not selected
      }
    }

    if (onlyLeaves) {
      selectSingle(option);
    } else if (!node.children) {
      select(option);
    }
  });

  setSelectedStatusForAncestors(value, value);
  return leaves.filter(isSelected);
}

export const selectSubtreeFromExternalValue = (options, value) =>
  syncToExternalValue(options, [value], false);

// expand and collapse items based on the following:
// - if all options are uncollapsed and fit on the page, uncollapse all options, otherwise
// - uncollapse only selected options
// for all items, remove search ()
export const setDefaultCollapsedState = (options, maxHeight, rowheight) => {
  // if height of all rows is less than maxHeight, uncollapse all
  const alwaysUncollapse = rowheight * options.length < maxHeight;
  alwaysUncollapse
    ? options.forEach(uncollapseReset)
    : uncollapseAllSelectedAndRemoveSearch(options);
};
